import React, { useState, useEffect } from 'react';

const FigtreeArchitectureDemo = ({ isProcessing = false, currentStage = '' }) => {
  const [activeNodes, setActiveNodes] = useState([]);
  const [dataFlow, setDataFlow] = useState([]);

  const architectureNodes = [
    {
      id: 'scraper',
      label: 'Web Scraper Tool',
      description: 'Multi-page content extraction',
      icon: '🕷️',
      position: { x: 15, y: 40 },
      type: 'tool',
      capabilities: ['Multi-page crawling', 'Content extraction', 'Brand detection', 'Amenity identification']
    },
    {
      id: 'crewai',
      label: 'CrewAI Framework',
      description: 'Multi-agent orchestration',
      icon: '🤖',
      position: { x: 35, y: 40 },
      type: 'framework',
      capabilities: ['Agent coordination', 'Task management', 'Sequential processing', 'Result aggregation']
    },
    {
      id: 'auditor',
      label: 'Web Auditor Agent',
      description: 'Website Analysis Specialist',
      icon: '🔍',
      position: { x: 53, y: 20 },
      type: 'agent',
      capabilities: ['Brand positioning analysis', 'Target audience detection', 'Content tone assessment', 'SEO evaluation']
    },
    {
      id: 'strategy',
      label: 'Strategy Agent',
      description: 'Content Strategy Expert',
      icon: '🎯',
      position: { x: 70, y: 20 },
      type: 'agent',
      capabilities: ['Market positioning', 'Audience prioritisation', 'Content framework', 'Competitive analysis']
    },
    {
      id: 'content',
      label: 'Content Generator Agent',
      description: 'Tourism Content Specialist',
      icon: '✍️',
      position: { x: 53, y: 80 },
      type: 'agent',
      capabilities: ['Australian English', 'Tourism expertise', 'Multi-format content', 'Brand-aligned writing']
    },
    {
      id: 'quality',
      label: 'Quality Agent',
      description: 'Content Quality Assurance',
      icon: '🏆',
      position: { x: 70, y: 80 },
      type: 'agent',
      capabilities: ['SEO optimisation', 'Readability analysis', 'Quality scoring', 'Improvement recommendations']
    },
    {
      id: 'weather',
      label: 'Weather Agent',
      description: 'Contextual weather insights',
      icon: '🌤️',
      position: { x: 92, y: 40 },
      type: 'agent-coming-soon',
      capabilities: ['Real-time conditions', 'Seasonal insights', 'Activity recommendations', 'Coming Soon']
    },
  ];

  const connections = [
    { from: 'scraper', to: 'crewai', label: 'Extracted Data' },
    { from: 'crewai', to: 'auditor', label: 'Orchestration' },
    { from: 'crewai', to: 'strategy', label: 'Coordination' },
    { from: 'crewai', to: 'content', label: 'Management' },
    { from: 'crewai', to: 'quality', label: 'Control' },
    { from: 'auditor', to: 'strategy', label: 'Analysis' },
    { from: 'strategy', to: 'content', label: 'Strategy' },
    { from: 'content', to: 'quality', label: 'Content' }
  ];

  useEffect(() => {
    if (isProcessing) {
      // Simulate CrewAI processing flow
      const stages = {
        'initializing': ['website', 'crewai'],
        'scraping': ['website', 'scraper', 'weather', 'crewai'],
        'auditing': ['website', 'scraper', 'weather', 'auditor', 'crewai'],
        'strategizing': ['website', 'scraper', 'weather', 'auditor', 'strategy', 'crewai'],
        'generating': ['website', 'scraper', 'weather', 'auditor', 'strategy', 'content', 'crewai'],
        'analyzing': ['website', 'scraper', 'weather', 'auditor', 'strategy', 'content', 'quality', 'crewai'],
        'complete': ['website', 'scraper', 'weather', 'auditor', 'strategy', 'content', 'quality', 'crewai', 'output']
      };

      const activeSet = stages[currentStage] || [];
      setActiveNodes(activeSet);
    } else {
      setActiveNodes([]);
    }
  }, [isProcessing, currentStage]);

  const NodeComponent = ({ node }) => {
    const isActive = activeNodes.includes(node.id);
    const nodeStyles = {
      input: { background: 'linear-gradient(135deg, #2C5F5D 0%, #4A8B87 100%)' },
      tool: { background: 'linear-gradient(135deg, #4A8B87 0%, #6CB955 100%)' }, 
      service: { background: 'linear-gradient(135deg, #6CB955 0%, #4A9B3A 100%)' },
      agent: { background: 'linear-gradient(135deg, #2C5F5D 0%, #6CB955 100%)' },
      'agent-coming-soon': { background: 'linear-gradient(135deg, #4A8B87 0%, #6CB955 100%)', opacity: '0.7' },
      framework: { background: 'linear-gradient(135deg, #6CB955 0%, #2C5F5D 100%)' },
      output: { background: 'linear-gradient(135deg, #4A8B87 0%, #2C5F5D 100%)' }
    };

    return (
      <div 
        className={`absolute transform -translate-x-1/2 -translate-y-1/2 transition-all duration-500 ${
          isActive ? 'scale-110 z-20' : 'scale-100 z-10'
        }`}
        style={{ left: `${node.position.x}%`, top: `${node.position.y}%` }}
      >
        <div className={`relative p-4 rounded-xl shadow-lg border-2 ${
          isActive ? 'border-yellow-400 shadow-xl' : 'border-white/20'
        } text-white min-w-44 max-w-48`} style={nodeStyles[node.type] || nodeStyles['agent']}>
          
          {/* Pulsing effect for active nodes */}
          {isActive && (
            <div className="absolute inset-0 rounded-xl bg-yellow-400/20 animate-ping"></div>
          )}
          
          <div className="relative">
            <div className="text-center mb-2">
              <span className="text-xl mb-1 block">{node.icon}</span>
              <h4 className="font-bold text-xs leading-tight">{node.label}</h4>
              <p className="text-xs opacity-75 mt-1">{node.description}</p>
            </div>
            
            {/* Coming Soon Badge */}
            {node.type === 'agent-coming-soon' && (
              <div className="absolute -top-2 -right-2">
                <span className="bg-yellow-500 text-black text-xs font-bold px-2 py-1 rounded-full">
                  Coming Soon
                </span>
              </div>
            )}

            {/* Capabilities list - only show top 3 */}
            {node.capabilities && (
              <div className="mt-2">
                <div className="text-xs font-semibold mb-1 opacity-80">Key Features:</div>
                <ul className="text-xs space-y-0.5">
                  {node.capabilities.filter(cap => cap !== 'Coming Soon').slice(0, 3).map((cap, idx) => (
                    <li key={idx} className="flex items-start">
                      <span className="mr-1 text-green-300">•</span>
                      <span className="opacity-80 leading-tight">{cap}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const ConnectionLine = ({ connection }) => {
    const fromNode = architectureNodes.find(n => n.id === connection.from);
    const toNode = architectureNodes.find(n => n.id === connection.to);
    
    if (!fromNode || !toNode) return null;
    
    const isActive = activeNodes.includes(connection.from) && activeNodes.includes(connection.to);
    
    return (
      <div className="absolute inset-0 pointer-events-none">
        <svg className="w-full h-full">
          <defs>
            <marker
              id={`arrowhead-${connection.from}-${connection.to}`}
              markerWidth="10"
              markerHeight="7"
              refX="9"
              refY="3.5"
              orient="auto"
            >
              <polygon
                points="0 0, 10 3.5, 0 7"
                fill={isActive ? '#6CB955' : '#4A8B87'}
                className="transition-colors duration-300"
              />
            </marker>
          </defs>
          <line
            x1={`${fromNode.position.x}%`}
            y1={`${fromNode.position.y}%`}
            x2={`${toNode.position.x}%`}
            y2={`${toNode.position.y}%`}
            stroke={isActive ? '#6CB955' : '#4A8B87'}
            strokeWidth={isActive ? '3' : '2'}
            strokeDasharray={isActive ? '0' : '5,5'}
            markerEnd={`url(#arrowhead-${connection.from}-${connection.to})`}
            className="transition-all duration-300"
          >
            {isActive && (
              <animate
                attributeName="stroke-dashoffset"
                values="0;10"
                dur="1s"
                repeatCount="indefinite"
              />
            )}
          </line>
        </svg>
      </div>
    );
  };

  return (
    <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-xl p-8 text-white relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #2C5F5D 0%, #1A1A1A 60%, #4A8B87 100%)' }}>
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0 bg-grid-pattern"></div>
      </div>
      
      <div className="relative">
        

        {/* Architecture visualization */}
        <div className="relative h-80 mb-8">
          {/* Render connections first */}
          {connections.map((connection, idx) => (
            <ConnectionLine key={idx} connection={connection} />
          ))}
          
          {/* Render nodes */}
          {architectureNodes.map(node => (
            <NodeComponent key={node.id} node={node} />
          ))}
        </div>

        {/* Key metrics and capabilities */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-8">
          <div className="backdrop-blur-sm rounded-lg p-4 text-center" style={{ backgroundColor: 'rgba(108, 185, 85, 0.2)' }}>
            <div className="text-3xl font-bold" style={{ color: '#ffffff' }}>4</div>
            <div className="text-sm" style={{ color: '#ffffff' }}>CrewAI Agents</div>
          </div>
          <div className="backdrop-blur-sm rounded-lg p-4 text-center" style={{ backgroundColor: 'rgba(108, 185, 85, 0.2)' }}>
            <div className="text-3xl font-bold" style={{ color: '#ffffff' }}>Multi-Agent</div>
            <div className="text-sm" style={{ color: '#ffffff' }}>Framework</div>
          </div>
          <div className="backdrop-blur-sm rounded-lg p-4 text-center" style={{ backgroundColor: 'rgba(108, 185, 85, 0.2)' }}>
            <div className="text-3xl font-bold" style={{ color: '#ffffff' }}>8+</div>
            <div className="text-sm" style={{ color: '#ffffff' }}>Content Types</div>
          </div>
          <div className="backdrop-blur-sm rounded-lg p-4 text-center" style={{ backgroundColor: 'rgba(108, 185, 85, 0.2)' }}>
            <div className="text-3xl font-bold" style={{ color: '#ffffff' }}>100%</div>
            <div className="text-sm" style={{ color: '#ffffff' }}>Tourism Focused</div>
          </div>
        </div>

        {/* Agent workflow description */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="rounded-lg p-4" style={{ backgroundColor: 'rgba(108, 185, 85, 0.1)' }}>
            <h4 className="text-lg font-semibold mb-3 flex items-center">
              <span className="mr-2">🔄</span>
              Agent Workflow
            </h4>
            <div className="space-y-2 text-sm">
              <div className="flex items-center">
                <span className="mr-2">1.</span>
                <span>Web Auditor analyses property website</span>
              </div>
              <div className="flex items-center">
                <span className="mr-2">2.</span>
                <span>Strategy Agent develops content framework</span>
              </div>
              <div className="flex items-center">
                <span className="mr-2">3.</span>
                <span>Content Generator creates tourism content</span>
              </div>
              <div className="flex items-center">
                <span className="mr-2">4.</span>
                <span>Quality Agent performs comprehensive review</span>
              </div>
            </div>
          </div>
          
          <div className="rounded-lg p-4" style={{ backgroundColor: 'rgba(108, 185, 85, 0.1)' }}>
            <h4 className="text-lg font-semibold mb-3 flex items-center">
              <span className="mr-2">🧠</span>
              AI Specialisation
            </h4>
            <div className="space-y-2 text-sm">
              <div>• Each agent has specialised tourism knowledge</div>
              <div>• Australian/NZ hospitality expertise</div>
              <div>• Multi-segment audience targeting</div>
              <div>• SEO and readability optimisation</div>
              <div>• Brand-aligned content generation</div>
            </div>
          </div>
        </div>

        {/* Technology stack */}
        <div className="mt-8 grid grid-cols-2 md:grid-cols-6 gap-4 text-center">
          <div className="rounded-lg p-3" style={{ backgroundColor: 'rgba(108, 185, 85, 0.1)' }}>
            <div className="text-lg">🤖</div>
            <div className="text-xs" style={{ color: '#ffffff' }}>CrewAI</div>
          </div>
          <div className="rounded-lg p-3" style={{ backgroundColor: 'rgba(108, 185, 85, 0.1)' }}>
            <div className="text-lg">🐍</div>
            <div className="text-xs" style={{ color: '#ffffff' }}>FastAPI</div>
          </div>
          <div className="rounded-lg p-3" style={{ backgroundColor: 'rgba(108, 185, 85, 0.1)' }}>
            <div className="text-lg">⚛️</div>
            <div className="text-xs" style={{ color: '#ffffff' }}>React</div>
          </div>
          <div className="rounded-lg p-3" style={{ backgroundColor: 'rgba(108, 185, 85, 0.1)' }}>
            <div className="text-lg">🧠</div>
            <div className="text-xs" style={{ color: '#ffffff' }}>OpenAI</div>
          </div>
          <div className="rounded-lg p-3" style={{ backgroundColor: 'rgba(108, 185, 85, 0.1)' }}>
            <div className="text-lg">🌤️</div>
            <div className="text-xs" style={{ color: '#ffffff' }}>Weather API</div>
          </div>
          <div className="rounded-lg p-3" style={{ backgroundColor: 'rgba(108, 185, 85, 0.1)' }}>
            <div className="text-lg">🕷️</div>
            <div className="text-xs" style={{ color: '#ffffff' }}>Web Scraping</div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .bg-grid-pattern {
          background-image: 
            linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px);
          background-size: 20px 20px;
        }
      `}</style>
    </div>
  );
};

export default FigtreeArchitectureDemo;